import { Chart } from "@highcharts/react";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { token } from "../../../../styled-system/tokens";
import { useAppSelector } from "../../../redux/hooks";
import { lightenColor } from "../../../utils/colors";
import { formatMetricValue, type FormatOptions } from "../../../utils/formatNumber";

const LIGHTEN_AMOUNT = 1.3;

export function PacingGauge({
  target,
  current,
  allowance = 0.15,
  formatOptions,
}: {
  target: number;
  current: number;
  allowance?: number;
  formatOptions: FormatOptions;
}) {
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  formatOptions.marketPlace ??= marketPlace;
  const gaugeMin = target * (1 - allowance);
  const gaugeMax = target * (1 + allowance);
  const gaugeCurrent = Math.max(gaugeMin, Math.min(current, gaugeMax));

  const options: Highcharts.Options = {
    chart: {
      inverted: true,
      height: 50,
      marginTop: 10,
      marginBottom: 30,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    title: {
      text: "",
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      bullet: {
        pointPadding: 0.25,
        borderWidth: 0,
        targetOptions: {
          color: token("colors.trellisPalette.4"),
          width: 17,
        },
      },
    },
    xAxis: {
      visible: false,
      height: 10,
      min: 0,
      max: 0,
      maxPadding: 0,
    },
    yAxis: [
      {
        offset: 0,
        gridLineWidth: 0,
        tickPositions: [gaugeMin, target, gaugeMax],
        min: gaugeMin,
        max: gaugeMax,
        plotBands: [
          {
            from: gaugeMin,
            to: gaugeMax,
            color: {
              linearGradient: { x1: 0, x2: 1, y1: 0, y2: 0 },
              stops: [
                [0, lightenColor(token("colors.trellisPalette.17"), LIGHTEN_AMOUNT)],
                [0.2, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [0.35, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [0.5, lightenColor(token("colors.trellisPalette.12"), LIGHTEN_AMOUNT)],
                [0.65, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [0.8, lightenColor(token("colors.trellisPalette.16"), LIGHTEN_AMOUNT)],
                [1, lightenColor(token("colors.trellisPalette.17"), LIGHTEN_AMOUNT)],
              ],
            },
          },
        ],
        labels: {
          formatter: function () {
            return formatMetricValue(this.value as number, formatOptions);
          },
        },
        title: {
          text: undefined,
        },
      },
    ],
    series: [
      {
        type: "bullet",
        clip: false, // Allow target marker to overflow plot area
        data: [
          {
            target: gaugeCurrent,
          },
        ],
        dataLabels: {
          enabled: true,
        },
      },
    ],
  };

  return <Chart options={options} />;
}
